import { useEffect, useRef, useState } from 'react';
import type { AnimationItem, LottiePlayer } from 'lottie-web';
import { hasNotoAnimation, loadNotoAnimation, restFrameOf } from './notoEmoji';
import './animatedEmoji.css';

/**
 * An emoji that moves (animated reactions, phase 1).
 *
 * Emoji with a Noto file (notoEmoji.ts) play once when they first scroll into
 * view, then stop on their still "rest" frame. Pointing at or focusing the
 * button they sit in plays them again. Everything else renders as the same
 * plain text as before, so an odd stored emoji is still only React text.
 *
 * Cost is bounded, as the design note measured (design-note-animated-
 * reactions.md section 6): the player (lottie-web light, SVG only, no
 * expressions) and each emoji file are loaded with import() the first time
 * one is needed; at most MAX_PLAYING animate at the same moment; a finished
 * one costs nothing per frame; every player is destroyed on unmount.
 *
 * Reduced motion is checked here in JS, because the CSS switch in tokens.css
 * stops CSS animation only. When the reader asks for less motion, nothing
 * ever plays and the still frame shows.
 *
 * The glyph is aria-hidden. Screen readers keep hearing the button's own
 * label, exactly as before.
 */

/** No more than this many emoji animate at once, across the whole page. */
export const MAX_PLAYING = 8;

let playingNow = 0;

/** How many emoji are animating right now. For tests. */
export function playingCount(): number {
  return playingNow;
}

let playerModule: Promise<LottiePlayer> | null = null;

function loadPlayer(): Promise<LottiePlayer> {
  playerModule ??= import('lottie-web/build/player/lottie_light').then(
    (mod) => mod.default,
    (error: unknown) => {
      // Let a later emoji try again rather than caching the failure.
      playerModule = null;
      throw error;
    },
  );
  return playerModule;
}

const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

function reduceQuery(): MediaQueryList | null {
  return typeof window.matchMedia === 'function' ? window.matchMedia(REDUCE_QUERY) : null;
}

/** True while the reader's system asks for reduced motion; follows changes. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => reduceQuery()?.matches ?? false);
  useEffect(() => {
    const query = reduceQuery();
    if (query === null) {
      return undefined;
    }
    const onChange = () => setReduced(query.matches);
    onChange();
    if (typeof query.addEventListener !== 'function') {
      return undefined;
    }
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/** One emoji's player. Plain object, so React state never holds the animation. */
class EmojiPlayer {
  private anim: AnimationItem | null = null;
  private rest = 0;
  private playing = false;
  private started = false;
  private dead = false;
  private reduced = false;

  constructor(
    private readonly emoji: string,
    private readonly box: HTMLElement,
    private readonly onReady: () => void,
  ) {}

  /** First time in view: load, show the still frame, then play once. */
  begin(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.load().then(
      () => this.play(),
      () => {
        // No player or no file: the plain text glyph simply stays.
      },
    );
  }

  private async load(): Promise<void> {
    const [lottie, data] = await Promise.all([loadPlayer(), loadNotoAnimation(this.emoji)]);
    if (this.dead || data === null) {
      return;
    }
    this.rest = restFrameOf(data);
    const anim = lottie.loadAnimation({
      container: this.box,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      animationData: data,
    });
    anim.addEventListener('complete', () => this.toRest());
    anim.goToAndStop(this.rest, true);
    this.anim = anim;
    this.onReady();
  }

  /** Play from the start, unless reduced, already playing, or the page is at the cap. */
  play(): void {
    if (this.anim === null || this.playing || this.reduced || playingNow >= MAX_PLAYING) {
      return;
    }
    this.playing = true;
    playingNow += 1;
    this.anim.goToAndPlay(0, true);
  }

  setReduced(reduced: boolean): void {
    this.reduced = reduced;
    if (reduced) {
      this.toRest();
    }
  }

  private release(): void {
    if (this.playing) {
      this.playing = false;
      playingNow = Math.max(0, playingNow - 1);
    }
  }

  private toRest(): void {
    this.release();
    this.anim?.goToAndStop(this.rest, true);
  }

  destroy(): void {
    this.dead = true;
    this.release();
    this.anim?.destroy();
    this.anim = null;
  }
}

function NotoEmoji({ emoji }: { emoji: string }) {
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const boxRef = useRef<HTMLSpanElement | null>(null);
  const playerRef = useRef<EmojiPlayer | null>(null);
  const [ready, setReady] = useState(false);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const root = rootRef.current;
    const box = boxRef.current;
    if (root === null || box === null) {
      return undefined;
    }
    const player = new EmojiPlayer(emoji, box, () => setReady(true));
    playerRef.current = player;

    // Play once on first view. Old browsers without the observer keep the
    // plain text glyph, as before.
    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer?.disconnect();
          player.begin();
        }
      });
      observer.observe(root);
    }

    // Replay when the button it sits in is pointed at or focused.
    const host = root.closest('button') ?? root.parentElement;
    const replay = () => player.play();
    host?.addEventListener('mouseenter', replay);
    host?.addEventListener('focusin', replay);

    return () => {
      observer?.disconnect();
      host?.removeEventListener('mouseenter', replay);
      host?.removeEventListener('focusin', replay);
      player.destroy();
      playerRef.current = null;
      setReady(false);
    };
  }, [emoji]);

  // Runs after the effect above, so a new player for a new emoji gets it too.
  useEffect(() => {
    playerRef.current?.setReduced(reduced);
  }, [reduced, emoji]);

  return (
    <span
      ref={rootRef}
      className={`animated-emoji${ready ? ' animated-emoji--ready' : ''}`}
      aria-hidden="true"
    >
      <span className="animated-emoji-text">{emoji}</span>
      <span ref={boxRef} className="animated-emoji-player" />
    </span>
  );
}

/** The emoji, animated when there is a Noto file for it, plain text when not. */
export function AnimatedEmoji({ emoji }: { emoji: string }) {
  if (!hasNotoAnimation(emoji)) {
    return <>{emoji}</>;
  }
  return <NotoEmoji emoji={emoji} />;
}
